// 표시 형식의 **적용 값 미리보기** 해소 — 저작 화면이 "그래서 지금 무엇이 적용되는지"를 값으로
// 보이게 하는 순수 층(컴포넌트 없음, co-located spec).
//
// 배경: 형식이 출처를 선언하게 된 뒤(scopeFrom, 2026-08-07) *어디서 오는지*는 보이지만
// *그래서 무엇이 적용되는지*는 여전히 경로 문자열만 보인다 — 오타는 "아무것도 안 나옴"으로만
// 드러나 저작자가 원인을 알 수 없다. rangeMap에만 있던 미리보기(RangeCriteriaPreview /
// RangeAnnotationPreview)를 **경로 표현식을 가진 나머지 형식**으로 넓힌다.
// 줄기: docs/work-streams/active/composer-format-authoring-ux.md P1
//
// ⚠️ **해소는 엔진 함수만 쓴다** — `withFormatScope` → `getByPath` → `toText`. rc index가 바로
// 그것을 요구한다("호스트가 형식 적용 밖에서 같은 스코프를 흉내 내야 할 때(저작 미리보기 등)
// 자체 해소를 만들지 말고 이걸 쓸 것"). 자기 판 해소를 만들면 **미리보기엔 보이는데 렌더는 빈
// 칸**인 드리프트가 조용히 생기고, 그 드리프트는 저작 화면에서는 영영 드러나지 않는다.
//
// ⚠️ **이 층이 못 재는 축**: 바인딩의 **값 자체**(`{value}`·map의 입력). 미리보기 채널은 해소된
// prefill 값 묶음이고 FormatFields 다섯 호출부 중 어느 것도 "이 칸의 값"을 넘기지 않는다(일괄
// 셀 편집·계산 호출부는 애초에 단일 값이 아니다). 그래서 `{value}`는 자리표로 남기고 map은
// **미매칭 시 동작**만 낸다 — 지어낸 값을 보여주는 것이 빈 미리보기보다 나쁘다.

import {
  getByPath,
  isEmptyValue,
  toText,
  withFormatScope,
  type BindingFormat,
} from '@safetyhub/report-composer';

/**
 * 경로 하나의 해소 결과.
 *
 * `unresolved`와 `empty`를 **가른다** — 엔진(`toText`)은 둘을 모두 ''로 뭉개지만 저작자에게는
 * 진단이 다르다: 전자는 "경로가 이 채널에 없다"(오타·스코프 착오·컨텍스트 미선택)이고 후자는
 * "경로는 맞고 값이 비었다"(정상일 수 있다). 뭉개면 오타가 빈 값으로 위장한다.
 */
export type PathPreview =
  | { state: 'resolved'; text: string }
  | { state: 'empty' }
  | { state: 'unresolved' };

/** 미리보기 채널 자체가 없을 때(컨텍스트 미선택) — 호출부가 안내 문구로 degrade하는 신호. */
export const NO_CHANNEL: PathPreview = { state: 'unresolved' };

/**
 * 형식의 경로 표현식 하나를 **원값으로** 읽는다 — 모든 kind가 공유하는 단 하나의 해소 경로.
 *
 * 스코프 얹기(`withFormatScope`)를 여기서 통과시키므로, 형식이 `scopeFrom`으로 선언한 이름을
 * 가리키는 경로도 렌더와 **같이** 읽힌다. 그 통과가 없으면 선언 축을 쓴 저작물에서만 미리보기가
 * 조용히 비어 "선언이 안 먹는다"는 오진을 만든다.
 *
 * 문자열이 아니라 **원값**이 필요한 소비처(구간 배열 = rangeMap rangesKey)가 있어 둘로 나눈다 —
 * 배열을 `toText`로 뭉개면 구간을 나열할 수 없다.
 */
export function readFormatScopeRaw(
  format: BindingFormat,
  path: string | undefined,
  previewValues: Record<string, unknown> | undefined,
): unknown {
  if (!path || !previewValues) return undefined;
  return getByPath(withFormatScope(format, previewValues), path);
}

/**
 * 위 원값을 **표시 문자열 + 진단 상태**로 — 경로 칸을 가진 형식들이 공유하는 규약.
 */
export function readFormatPath(
  format: BindingFormat,
  path: string | undefined,
  previewValues: Record<string, unknown> | undefined,
): PathPreview {
  if (!path || !previewValues) return NO_CHANNEL;
  const raw = readFormatScopeRaw(format, path, previewValues);
  if (raw === undefined) return { state: 'unresolved' };
  if (isEmptyValue(raw)) return { state: 'empty' };
  // 객체/배열은 format 비대상이라 엔진이 String()에 위임한다 — 그 결과를 그대로 보인다
  // (저작자가 "객체를 가리켰다"를 눈으로 알아야 경로를 고칠 수 있다).
  const text = toText(raw);
  return text.trim() === '' ? { state: 'empty' } : { state: 'resolved', text };
}

// ---------------------------------------------------------------------------
// unit — 해소된 단위
// ---------------------------------------------------------------------------

/**
 * unit 형식이 실제로 붙일 단위.
 *
 * ⚠️ 엔진은 `unitPath`가 **지정돼 있으면 그쪽만** 본다(`format.unitPath ? … : (format.unit ?? '')`).
 * 그래서 둘을 다 적으면 고정 `unit`은 **죽은 저작**이고, 경로가 해소되지 않으면 단위가 아예 붙지
 * 않는다(고정값으로 폴백하지 않는다). 그 비대칭이 화면에 보이지 않으면 "unit을 적었는데 안 나온다"가
 * 된다 — `staticIgnored`가 그 사실을 들고 있다.
 */
export type UnitPreview =
  | { source: 'path'; path: string; unit: PathPreview; staticIgnored: string | undefined }
  | { source: 'static'; unit: string }
  | { source: 'none' };

export function resolveUnitPreview(
  format: Extract<BindingFormat, { kind: 'unit' }>,
  previewValues: Record<string, unknown> | undefined,
): UnitPreview {
  if (format.unitPath) {
    return {
      source: 'path',
      path: format.unitPath,
      unit: readFormatPath(format, format.unitPath, previewValues),
      // 빈 문자열은 "적지 않음"과 같아 무시 대상으로 세지 않는다(엔진의 `?? ''`와 같은 결).
      staticIgnored: format.unit ? format.unit : undefined,
    };
  }
  return format.unit ? { source: 'static', unit: format.unit } : { source: 'none' };
}

// ---------------------------------------------------------------------------
// switch — 그래서 선택되는 케이스
// ---------------------------------------------------------------------------

/**
 * switch 형식이 **지금 고를** 케이스.
 *
 * ⚠️ 엔진은 `on`이 해소되지 않아도 멈추지 않는다 — `toText(undefined)`가 ''이므로 **빈 문자열을
 * 케이스 키로** 쓰고, 대개 미매칭이라 조용히 `default`로 떨어진다. 즉 경로 오타의 증상이
 * "분기가 안 바뀐다"라서 저작자는 `cases`를 의심한다. `key`가 엔진이 실제로 쓰는 그 문자열이다.
 */
export interface SwitchPreview {
  /** `on` 경로 해소 결과(진단용 — 미해소/빈값/값). */
  on: PathPreview;
  /** 엔진이 케이스 키로 쓰는 문자열(미해소·빈값이면 ''). */
  key: string;
  /** `cases`에 그 키가 **선언돼 있는가**(값이 빈 문자열인 케이스도 매칭이다). */
  matched: boolean;
  /** 선택되는 템플릿 — 미매칭 + `default` 미지정이면 undefined(표시할 템플릿 자체가 없다). */
  template: string | undefined;
  /** 미매칭 + `default` 미지정 + `empty` 지정 → 통째 `empty`로 대체된다. */
  fallsBackToEmpty: boolean;
}

export function resolveSwitchPreview(
  format: Extract<BindingFormat, { kind: 'switch' }>,
  previewValues: Record<string, unknown> | undefined,
): SwitchPreview {
  const on = readFormatPath(format, format.on, previewValues);
  const key = on.state === 'resolved' ? on.text : '';
  const matched = Object.prototype.hasOwnProperty.call(format.cases, key);
  const template = matched ? format.cases[key] : format.default;
  return {
    on,
    key,
    matched,
    template,
    fallsBackToEmpty: !matched && format.default === undefined && format.empty !== undefined,
  };
}

// ---------------------------------------------------------------------------
// template — 치환 결과
// ---------------------------------------------------------------------------

/**
 * `{value}` 자리표 — 이 층은 바인딩 값을 모른다(모듈 머리말의 "못 재는 축").
 * 치환 결과에 그대로 남겨 저작자가 "여기에 이 칸의 값이 들어간다"를 읽게 한다.
 */
export const VALUE_PLACEHOLDER = '〈이 칸의 값〉';

/** 템플릿이 참조하는 자리 하나. */
export interface TemplateRefPreview {
  /** `{}` 안의 표현식(공백 제거 전 원문 — 저작자가 적은 그대로 보여 대조하게 한다). */
  expr: string;
  /** 엔진이 쓰는 키(`expr.trim()`). */
  key: string;
  /** `{value}`(이 칸의 값)인가 — 그렇다면 이 층에서는 해소하지 않는다. */
  isValue: boolean;
  /** `{value}`가 아닌 자리의 해소 결과. */
  text: PathPreview | undefined;
}

export interface TemplatePreview {
  refs: TemplateRefPreview[];
  /** `{value}`만 자리표로 남긴 치환 결과(엔진 applyTemplate과 같은 치환). */
  result: string;
  /** 참조가 하나도 없는 정적 문구인가 — 미리보기가 원문과 같아 보일 이유를 호출부가 말하게 한다. */
  staticOnly: boolean;
}

/** 엔진 applyTemplate과 **같은** 치환 문법(`{expr}`) — 둘이 갈리면 미리보기가 거짓이 된다. */
const PLACEHOLDER_RE = /\{([^}]+)\}/g;

export function resolveTemplatePreview(
  template: string,
  format: BindingFormat,
  previewValues: Record<string, unknown> | undefined,
): TemplatePreview {
  const refs: TemplateRefPreview[] = [];
  // 저작자 이스케이프 `\n`(백슬래시+n 두 글자) → 실제 개행. 엔진 applyTemplate이 치환 **전에**
  // 하는 일이라 순서를 맞춘다(그래야 미리보기 줄 수가 렌더와 같다).
  const result = template.replace(/\\n/g, '\n').replace(PLACEHOLDER_RE, (_m, expr: string) => {
    const key = expr.trim();
    if (key === 'value') {
      refs.push({ expr, key, isValue: true, text: undefined });
      return VALUE_PLACEHOLDER;
    }
    const text = readFormatPath(format, key, previewValues);
    refs.push({ expr, key, isValue: false, text });
    return text.state === 'resolved' ? text.text : '';
  });
  return { refs, result, staticOnly: refs.length === 0 };
}

// ---------------------------------------------------------------------------
// map — 미매칭 시 동작
// ---------------------------------------------------------------------------

/**
 * map 형식이 **케이스에 없는 값을 만났을 때** 하는 일.
 *
 * map에는 경로 표현식이 없다 — 입력이 바인딩 값 자체라 이 채널로는 "현재 값 매칭"을 **원리적으로**
 * 보일 수 없다(모듈 머리말). 대신 조용한 실패 하나를 드러낸다: `fallback`이 빈 문자열이면 미매칭이
 * **빈 칸**으로 나와 "값이 없는 칸"과 구별되지 않는다(열 하나가 통째로 빈 것처럼 보인다).
 * `fallback` 미지정이면 원값이 그대로 나오므로 적어도 흔적은 남는다.
 */
export type MapMissBehavior =
  | { kind: 'passthrough' }
  | { kind: 'blank' }
  | { kind: 'label'; label: string };

export function resolveMapMissBehavior(
  format: Extract<BindingFormat, { kind: 'map' }>,
): MapMissBehavior {
  if (format.fallback === undefined) return { kind: 'passthrough' };
  return format.fallback === '' ? { kind: 'blank' } : { kind: 'label', label: format.fallback };
}
